// src/services/gateVitalicio.js -- o gate de acesso vitalicio, RTDB ou API.
//
// Plano §2.1: hoje o navegador decide e o navegador grava (ScreenOne.vue:70-128
// antes da F2). A F2 poe a mesma regra atras de uma interface com duas
// implementacoes e um modo de sombra, para medir divergencia antes do corte.
//
// A regra, na ordem exata em que o bundle a aplicava e em que o backend a
// reproduz (dashboard/gate_vitalicio.py:1-21):
//   1. `users-vitalicio/<tel>.acessoVitalicio` -> libera                 (origem `flag`)
//   2. soma de HOJE (America/Sao_Paulo) >= limiar -> libera E grava flag (`deposito_hoje`)
//   3. soma de ALGUM dia isolado >= limiar -> libera SEM gravar flag     (`deposito_qualquer_dia`)
//   4. senao -> `missing-deposit`
//
// Interface das tres implementacoes:
//   avaliar(telefone, ref) -> { vitalicio, origem, totalHoje, totalGeral,
//                               maiorTotalDiario, limiar }
//   conceder(telefone, ref) -> grava a flag (so faz sentido no ramo 2)
import { carregarPecasDoRtdb } from './rtdbSobDemanda.js';
import { hashCurto, registrarDivergencia, tolerarFalhaDaApi } from './sombra.js';

export const MODOS_DE_GATE = { RTDB: 'rtdb', SOMBRA: 'sombra', API: 'api' };

export const ORIGEM = {
  FLAG: 'flag',
  DEPOSITO_HOJE: 'deposito_hoje',
  DEPOSITO_QUALQUER_DIA: 'deposito_qualquer_dia',
};

const ROTA_VITALICIO = '/dashboard/api/vitalicio/';
const ROTA_LIMIAR = '/accounts/api/threshold/';
const LIMIAR_PADRAO = 50;

/**
 * So digitos -- e' o que `cleanPhone` ja faz no input (ScreenOne.vue:43-45) e o
 * primeiro passo de `normalizar_telefone` no backend (dashboard/telefone.py:34).
 *
 * De proposito NAO tira o DDI 55: quem digita '+5511...' hoje erra a chave do
 * RTDB, e o GateRtdb tem que continuar errando igual enquanto for ele quem
 * decide. Quem amplia o acerto e' o servidor (telefone.py:35-37), no modo `api`.
 *
 * Exemplo:
 *   normalizarTelefone('(11) 95721-8700')  // '11957218700'
 */
export function normalizarTelefone(bruto) {
  return String(bruto === null || bruto === undefined ? '' : bruto).replace(/\D/g, '');
}

function rotaDoTelefone(telefone) {
  return `${ROTA_VITALICIO}${encodeURIComponent(telefone)}/`;
}

function parametrosDeRef(ref) {
  return ref ? { ref } : {};
}

function resultadoNegado(limiar) {
  return {
    vitalicio: false, origem: null, totalHoje: 0, totalGeral: 0,
    maiorTotalDiario: 0, limiar,
  };
}

/** Limiar do afiliado, a chamada que o front ja fazia (ScreenOne.vue:46-51). */
export class LimiarDoAfiliadoHttp {
  constructor(http) {
    this.http = http;
  }

  async buscar(ref) {
    const resposta = await this.http.get(ROTA_LIMIAR + ref);
    return Number(resposta.data.threshold);
  }
}

export class GateRtdb {
  /**
   * @param {{lerNo: Function, gravarNo: Function}} rtdb  cliente de rtdbCliente.js
   * @param {{buscar: Function}} limiares  limiar por afiliado
   * @param {{hoje: Function, diaDe: Function}} calendario  dia civil de Sao Paulo
   */
  constructor(rtdb, limiares, calendario) {
    this.rtdb = rtdb;
    this.limiares = limiares;
    this.calendario = calendario;
  }

  async avaliar(telefone, ref) {
    const numero = normalizarTelefone(telefone);
    const limiar = await this.limiares.buscar(ref);
    const cadastro = await this.rtdb.lerNo(`users-vitalicio/${numero}`);
    if (cadastro && cadastro.acessoVitalicio) {
      return this._peloCadastro(limiar);
    }
    return this._pelosDepositos(await this.rtdb.lerNo(`data/${numero}`), limiar);
  }

  async conceder(telefone, ref) { // eslint-disable-line no-unused-vars
    // `ref` nao entra aqui: o no do RTDB e' `{acessoVitalicio:true}` e nada mais
    // (ScreenOne.vue:118). Quem guarda o afiliado da concessao e' o backend.
    await this.rtdb.gravarNo(`users-vitalicio/${normalizarTelefone(telefone)}`,
      { acessoVitalicio: true });
  }

  // Com a flag ligada o front NUNCA lia `/data` (ScreenOne.vue:75-86): os totais
  // sao desconhecidos, e inventar uma segunda leitura mudaria o custo do gate.
  _peloCadastro(limiar) {
    return {
      vitalicio: true, origem: ORIGEM.FLAG, totalHoje: 0, totalGeral: 0,
      maiorTotalDiario: 0, limiar,
    };
  }

  _pelosDepositos(eventos, limiar) {
    if (!eventos) {
      return resultadoNegado(limiar);
    }
    const porDia = this._totaisPorDia(eventos);
    const totais = Object.values(porDia);
    const totalHoje = porDia[this.calendario.hoje()] || 0;
    const maiorTotalDiario = totais.length ? Math.max(...totais) : 0;
    return {
      vitalicio: totalHoje >= limiar || maiorTotalDiario >= limiar,
      origem: origemDosDepositos(totalHoje, maiorTotalDiario, limiar),
      totalHoje,
      totalGeral: totais.reduce((soma, valor) => soma + valor, 0),
      maiorTotalDiario,
      limiar,
    };
  }

  _totaisPorDia(eventos) {
    const porDia = {};
    Object.keys(eventos).forEach((chave) => {
      const evento = eventos[chave];
      const dia = this.calendario.diaDe(evento.timestamp);
      porDia[dia] = (porDia[dia] || 0) + parseFloat(evento.deposito);
    });
    return porDia;
  }
}

function origemDosDepositos(totalHoje, maiorTotalDiario, limiar) {
  if (totalHoje >= limiar) {
    return ORIGEM.DEPOSITO_HOJE;
  }
  return maiorTotalDiario >= limiar ? ORIGEM.DEPOSITO_QUALQUER_DIA : null;
}

export class GateApi {
  /** @param {{get: Function, post: Function}} http  instancia axios de services/api.js */
  constructor(http) {
    this.http = http;
  }

  async avaliar(telefone, ref) {
    const numero = normalizarTelefone(telefone);
    const resposta = await this.http.get(rotaDoTelefone(numero),
      { params: parametrosDeRef(ref) });
    return resultadoDaApi(resposta.data);
  }

  async conceder(telefone, ref) {
    // O POST reavalia no servidor antes de gravar (views_vitalicio.py:49-57) --
    // o front nao consegue conceder vitalicio a quem nao cumpriu a regra.
    const numero = normalizarTelefone(telefone);
    const resposta = await this.http.post(rotaDoTelefone(numero), parametrosDeRef(ref));
    return resultadoDaApi(resposta.data);
  }
}

// O backend serializa Decimal como string ('120.00', views_vitalicio.py:112-113).
function resultadoDaApi(corpo) {
  return {
    vitalicio: Boolean(corpo.vitalicio),
    origem: corpo.origem || null,
    totalHoje: Number(corpo.total_hoje),
    totalGeral: Number(corpo.total_geral),
    maiorTotalDiario: Number(corpo.maior_total_diario),
    limiar: Number(corpo.limiar),
  };
}

export class GateSombra {
  constructor(gateRtdb, gateApi, registrar = registrarDivergencia) {
    this.gateRtdb = gateRtdb;
    this.gateApi = gateApi;
    this.registrar = registrar;
  }

  /** Chama os dois, DECIDE pela RTDB e loga a divergencia. Erro da API nao vaza. */
  async avaliar(telefone, ref) {
    const [daRtdb, daApi] = await Promise.all([
      this.gateRtdb.avaliar(telefone, ref),
      tolerarFalhaDaApi(() => this.gateApi.avaliar(telefone, ref)),
    ]);
    this._compararDecisao(telefone, daRtdb, daApi);
    return daRtdb;
  }

  /** Grava nos dois lados: o dual-run precisa de `dashboard_lifetimeflag` quente na F3. */
  async conceder(telefone, ref) {
    const [daRtdb] = await Promise.all([
      this.gateRtdb.conceder(telefone, ref),
      tolerarFalhaDaApi(() => this.gateApi.conceder(telefone, ref)),
    ]);
    return daRtdb;
  }

  // Compara so `vitalicio` e `origem`: sao os campos que mudam o que o usuario
  // ve. Os totais divergem por construcao no ramo `flag`, onde a RTDB nao le
  // `/data` e devolve 0 -- compara-los encheria o log de ruido esperado.
  _compararDecisao(telefone, daRtdb, daApi) {
    if (daApi && daRtdb.vitalicio === daApi.vitalicio && daRtdb.origem === daApi.origem) {
      return;
    }
    this.registrar('gate_sombra_divergencia', {
      telefone_hash: hashCurto(normalizarTelefone(telefone)),
      rtdb: daRtdb,
      api: daApi,
    });
  }
}

const gatesPorModo = new Map();

/**
 * Gate do modo de build. Default `rtdb` -- nenhum build muda de comportamento
 * sem que alguem escreva VUE_APP_GATE.
 *
 * Exemplo:
 *   const gate = await criarGate();
 *   const resultado = await gate.avaliar('11957218700', '01hshpaw');
 */
export async function criarGate(modo = process.env.VUE_APP_GATE || MODOS_DE_GATE.RTDB) {
  if (!gatesPorModo.has(modo)) {
    gatesPorModo.set(modo, await montarGate(modo));
  }
  return gatesPorModo.get(modo);
}

async function montarGate(modo) {
  // Valida antes de carregar qualquer coisa: modo escrito errado tem que dizer
  // isso, e nao morrer com "RTDB indisponivel" num build feito em modo api.
  if (!Object.values(MODOS_DE_GATE).includes(modo)) {
    throw new Error(`VUE_APP_GATE=${modo} desconhecido. Esperado: rtdb|sombra|api`);
  }
  const { default: http } = await import('./api.js');
  if (modo === MODOS_DE_GATE.API) {
    return new GateApi(http);
  }
  const { rtdb, calendario } = await carregarPecasDoRtdb();
  const gateRtdb = new GateRtdb(rtdb, new LimiarDoAfiliadoHttp(http), calendario);
  return modo === MODOS_DE_GATE.RTDB
    ? gateRtdb
    : new GateSombra(gateRtdb, new GateApi(http));
}

export { LIMIAR_PADRAO };
